<template>
    <p-modal
        name="productSalesModal"
        label="Product Sales"
        ref="productSales"
        size="lg"
        @ok.prevent="handleModalOk"
    >
        <template v-slot:default>
            <p-form ref="productSalesForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>Item Sales</p>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range-widget :date-range-options="reportParameters" />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card class='mb-2 ps-2 py-2' no-body>
                                <label class='text-sm' v-if='dccSelectorHelpText'>{{dccSelectorHelpText}}</label>
                                <DCCsSelector ref="dccSelector" class='container-fluid' @selection-change='handleDccSelectionChange($event)' />
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
               <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="handleCancel"
                        >Cancel</p-button
                    >
                    <b-overlay :show="isBusy"
                                rounded
                                opacity="0.5"
                                spinner-small
                                spinner-variant="primary"
                                class="d-inline-block ms-2">
                        <b-dropdown text="Export" 
                                    variant="outline-primary">
                            <b-dropdown-item @click="ok">CSV</b-dropdown-item>
                        </b-dropdown>
                    </b-overlay>
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import DCCsSelector from '@/components/DCCsSelector.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';


export default {
    mixins: [ReportTemplates],
    components: { DCCsSelector, UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Product Sales',
            reportParameters: {
                dccs: [],
                startDate: null,
                endDate: null,
                dateOption: 0,
                locationId: null,
                sortOrder: 'dcc'
            }
        };
    },
    computed: {
        dccSelectorHelpText() {
            return this.reportParameters.dccs.length === 0
                ? 'ALL DCCs will be included in the report, you may select individual DCCs below.'
                : '';
        }
    },
    methods: {
        resetForm() {
            this.reportParameters.dccs = [];
            this.reportParameters.dateOption = 0;
            this.reportParameters.startDate = new Date().toISOString();
            this.reportParameters.endDate = new Date().toISOString();
            if (this.$refs.dccSelector) {
                this.$refs.dccSelector.clearSelections();
            }
        },
        async show() {
            this.resetForm();
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            await this.loadUserReportTemplates();
            this.$refs.productSales.show();
        },
        // DCCsSelector works in the {dccId} shape it emits; this report stores plain ids, so they
        // have to be wrapped back up on the way in.
        toDccSelection(dccIds) {
            return (dccIds ?? []).map(dccId => ({ dccId }));
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. The DCCs are the only saved reference —
        // the dates are literal values and sortOrder is fixed, with no control to change it.
        async validateReportParameters(savedParameters) {
            return this.$refs.dccSelector.hasAllDccIds(this.toDccSelection(savedParameters.dccs));
        },
        // ReportTemplates hook: DCCsSelector owns its own tree selection, so the restored ids have
        // to be pushed back into it. Called unconditionally — a saved template with no DCCs has to
        // clear whatever is selected now, not be skipped.
        async onUserReportTemplateApplied() {
            await this.$refs.dccSelector.restoreSelections(this.toDccSelection(this.reportParameters.dccs));
        },
        handleCancel() {
            this.resetForm();
            this.$refs.productSales.hide();
        },
        async handleModalOk() {
            this.isBusy = true;

            // The current location always wins: a restored template carries the location that was
            // active when it was saved, which would otherwise run the report for the wrong one.
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }

            await axios
                .post(
                    'sales/exportproductsalescsv',
                    this.reportParameters,
                    {
                        responseType: 'blob'
                    }
                ).then(result => {
                    downloadFileToBrowser(
                        result.data,
                        'Product Sales.csv',
                        'application/csv'
                    );
                    this.$appSuccess(
                            `Product Sales report generated successfully.`
                        );
                        this.$refs.productSales.hide();
                }).finally(() => {
                    this.isBusy = false;
                });
        },
        handleDccSelectionChange($event){
            this.reportParameters.dccs = $event.map(x => x.dccId)
        }
    }
}
</script>
